import type { Link, Connection } from '@wix/editor-platform-sdk-types'
import type { FieldType } from '@wix/wix-data-schema-types'

import type {
  BehaviorType,
  DataBindingFilterProperty,
  DataBindingProperty,
} from '@wix/data-binding-registry-sdk'
import type { Action as TpaAction } from '@wix/data-binding-tpa-actions'
import type { DATA } from '@wix/component-protocol/schema'

type EditorConnection<Config extends object = Connection['config']> = Omit<
  Connection,
  'config'
> & {
  config: Config
}

export type DataBindingConnection =
  EditorConnection<DataBindingConnectionConfig>

type DataBindingConnectionConfig = {
  properties?: Record<PropName, EditorFieldBindingConfig>
  events?: Record<EventName, EditorActionBindingConfig>
  behaviors?: EditorBehaviorBindingConfig[]
  userInputFilter?: EditorUserFilterBindingConfig
  expressions?: Record<PropName, EditorExpressionBindingConfig>
  totalCount?: EditorTotalCountBindingConfig
}
export type ConnectionConfig = DataBindingConnectionConfig

type LiteralProp = string
type DynamicProp = `$${string}`
type PropName = LiteralProp | DynamicProp
type EventName = 'onClick' | 'onChange'

interface EditorFieldBindingConfig {
  fieldName: string
  linkTarget?: LinkTarget
  format?: string
  linkRel?: string
}

export type DatasetActionType =
  | 'save'
  | 'loadMore'
  | 'resetUserFilter'
  | 'applyUserInputFilters'
  | 'nextDynamicPage'
  | 'nextPage'
  | 'next'
  | 'previousDynamicPage'
  | 'previousPage'
  | 'previous'
  | 'new'
  | 'revert'
  | 'remove'

interface EditorActionBindingConfig {
  action: DatasetActionType | TpaAction
  postAction?: PostAction
}

interface EditorBehaviorBindingConfig {
  type: BehaviorType
}

interface EditorUserFilterBindingConfig {
  fieldName: string
  prop: string
  condition?: USER_INPUT_FILTER_CONDITIONS
}
interface EditorExpressionBindingConfig {
  expression: string
}

interface EditorTotalCountBindingConfig {
  prop: PropName
}

export type LinkTarget = '_blank' | '_self'

export interface FieldBindingConfig {
  type: 'Field'
  fieldPath: string
  fieldType?: FieldType
  prop: string
  format?: string
  linkTarget?: LinkTarget
  linkRel?: string
}

export type PostSubmitConfig =
  | { navigate: { linkObject: Link } }
  | { navigate: { fieldName: string } }

export type PostAction = PostSubmitConfig & { linkDisplayText?: string }

export type ActionBindingConfig = {
  type: 'Action'
  event: string
  action: string
  postAction: PostAction | null | undefined
}

export type BehaviorBindingConfig = {
  type: 'Behavior'
  behavior: BehaviorType
}

export enum USER_INPUT_FILTER_CONDITIONS {
  EQUALS = 'eq',
  NOT_EQUALS = 'ne',
  INCLUDES_ANY = 'includesAny',
  EXCLUDES_ANY = 'excludesAny',
  INCLUDES_ALL = 'includesAll',
  GREATER_THAN_OR_EQUAL = 'ge',
  LESS_THAN_OR_EQUAL = 'le',
  IN_BETWEEN = 'inBetween',
  OUTSIDE_OF = 'outsideOf',
}

export type FilterBindingConfig = {
  type: 'Filter'
  prop: string
  fieldPath: string
  condition?: USER_INPUT_FILTER_CONDITIONS
}

export type ExpressionBindingConfig = {
  type: 'Expression'
  prop: string
  expression: string
}

export type TotalCountBindingConfig = {
  type: 'TotalCount'
  prop: string
}

export type BindingConfig =
  | FieldBindingConfig
  | ActionBindingConfig
  | BehaviorBindingConfig
  | FilterBindingConfig
  | ExpressionBindingConfig
  | TotalCountBindingConfig

export type BindingDefinition = DataBindingProperty | DataBindingFilterProperty

export interface Binding<ConfigType extends BindingConfig = BindingConfig> {
  definition: BindingDefinition
  config: ConfigType | undefined | null
}

type BuilderDataType = typeof DATA.DATA_TYPE
type BuilderAtomicDataType = `${BuilderDataType['link' | 'text' | 'richText']}`
type BuilderParentDataType = `${BuilderDataType['a11y' | 'image']}`

type BuilderDataBindingItemKey = string
// Generated by the `encode` method of `BindingKeyManager` from
// `@wix/binding-key-manager`.
//
// At the time of writing, it follows this format:
// `dataItem::optionalInnerElementPath/level1/levelN::id:optionalSubItemId`.
//
// For example, `dataItem::::label::`.

export type BuilderDataBindingConnectionConfig = {
  type: 'binding'
  byItem: Record<BuilderDataBindingItemKey, BuilderDataBindingItem>
}

export type BuilderDataBindingItem = {
  config: BuilderDataBindingItemConfig
  icon: string
  target: string
}

export type BuilderConnection = Omit<Connection, 'config'> & {
  config: BuilderDataBindingConnectionConfig
}

type BuilderDataBindingItemConfigBase = {
  fieldKey: string
}

type BuilderDataBindingItemConfigWithDataType<
  DataType extends BuilderAtomicDataType,
> = BuilderDataBindingItemConfigBase & {
  dataType: DataType
}

type BuilderDataBindingConfigWithParentDataType<
  ParentDataType extends BuilderParentDataType,
> = BuilderDataBindingItemConfigBase & {
  parentDataType: ParentDataType
}

type DateFormatConfig = {
  format: {
    params: {
      dateFormat: string // TODO: import from wix-code-formatting requires declaring the types there
    }
    type: 'datetime'
  }
}

type LinkConfig = {
  data?: {
    rel: string
    target: LinkTarget
  }
}

type BuilderDataBindingItemConfig =
  | (BuilderDataBindingItemConfigWithDataType<'richText' | 'text'> &
      DateFormatConfig)
  | (BuilderDataBindingItemConfigWithDataType<'link'> & LinkConfig)
  | BuilderDataBindingConfigWithParentDataType<'image' | 'a11y'>
